import z from 'zod'
import { JobStatusSchema } from './schemas'

const Uuid = z.string().uuid()
const IsoDateTime = z.string().datetime({ offset: true })

export const JobReviewExpectedItemSchema = z.object({
  productVariantId: Uuid,
  productVariantName: z.string(),
  productCode: z.string(),
  sku: z.string(),
  type: z.enum(['meal', 'extra']),
  category: z.string().nullable(),
  weight: z.number().nullable(),
  barcode: z.string(),
  quantity: z.number().int().nonnegative(),
})
export type JobReviewExpectedItem = z.infer<typeof JobReviewExpectedItemSchema>

export const JobReviewExpectedSchema = z.object({
  items: z.array(JobReviewExpectedItemSchema),
  totalUnits: z.number().int().nonnegative(),
})
export type JobReviewExpected = z.infer<typeof JobReviewExpectedSchema>

export const JobReviewPackedItemSchema = z.object({
  productVariantId: Uuid,
  productVariantName: z.string(),
  quantity: z.number().int().nonnegative(),
})
export type JobReviewPackedItem = z.infer<typeof JobReviewPackedItemSchema>

export const JobReviewPackedSchema = z.object({
  items: z.array(JobReviewPackedItemSchema),
  totalUnits: z.number().int().nonnegative(),
  auditAvailable: z.literal(true),
  auditCapturedAt: IsoDateTime,
})
export type JobReviewPacked = z.infer<typeof JobReviewPackedSchema>

export const JobReviewMismatchSchema = z.object({
  productVariantId: Uuid,
  productVariantName: z.string(),
  expectedQty: z.number().int().nonnegative(),
  packedQty: z.number().int().nonnegative(),
  delta: z.number().int(),
})
export type JobReviewMismatch = z.infer<typeof JobReviewMismatchSchema>

export const JobReviewSummaryHappySchema = z.object({
  auditAvailable: z.literal(true),
  matched: z.number().int().nonnegative(),
  mismatched: z.number().int().nonnegative(),
  validationErrors: z.number().int().nonnegative(),
  mismatches: z.array(JobReviewMismatchSchema),
})
export type JobReviewSummaryHappy = z.infer<typeof JobReviewSummaryHappySchema>

export const JobReviewSummaryStaleSchema = z.object({
  auditAvailable: z.literal(false),
  matched: z.null(),
  mismatched: z.null(),
  validationErrors: z.null(),
  mismatches: z.array(JobReviewMismatchSchema).length(0),
})
export type JobReviewSummaryStale = z.infer<typeof JobReviewSummaryStaleSchema>
export const JobReviewSummarySchema = z.discriminatedUnion('auditAvailable', [
  JobReviewSummaryHappySchema,
  JobReviewSummaryStaleSchema,
])
export type JobReviewSummary = z.infer<typeof JobReviewSummarySchema>

export const JobReviewScanTimelineActionSchema = z.enum([
  'started',
  'scanned',
  'paused',
  'unpaused',
  'restarted',
  'packed',
  'early_finish',
  'overrode',
])
export type JobReviewScanTimelineAction = z.infer<typeof JobReviewScanTimelineActionSchema>

export const JobReviewValidationErrorSchema = z.object({
  errorType: z.string().min(1),
  detailedError: z.string(),
})
export type JobReviewValidationError = z.infer<typeof JobReviewValidationErrorSchema>

export const JobReviewScanTimelineEntrySchema = z.object({
  action: JobReviewScanTimelineActionSchema,
  at: IsoDateTime,
  pnpWorkerId: Uuid,
  resolvedBy: z.string().nullable(),
  barcode: z.string().optional(),
  productType: z.string().optional(),
  productVariantId: Uuid.optional(),
  validationError: JobReviewValidationErrorSchema.nullable().optional(),
  // Session-aware forensics (optional for back-compat with pre-rollout audits).
  sessionId: z.string().optional(),
  deviceId: z.string().nullable().optional(),
})
export type JobReviewScanTimelineEntry = z.infer<typeof JobReviewScanTimelineEntrySchema>

export const JobReviewSessionSchema = z.object({
  sessionId: z.string(),
  deviceId: z.string().nullable(),
  pnpWorkerId: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  startedAt: IsoDateTime.nullable(),
  endedAt: IsoDateTime.nullable(),
  terminalAction: JobReviewScanTimelineActionSchema.nullable(),
  packedUnits: z.number().int().nonnegative(),
  boxingStationId: z.number().int().positive().nullable(),
})
export type JobReviewSession = z.infer<typeof JobReviewSessionSchema>

// Authoritative packer for the job (assigned pnp_workers row), independent of the
// per-action pnpWorkerId (a Cognito sub that frequently misses the lookup).
export const JobReviewPackerSchema = z.object({
  pnpWorkerId: z.string().nullable(),
  name: z.string().nullable(),
})
export type JobReviewPacker = z.infer<typeof JobReviewPackerSchema>

const JobReviewBaseSchema = z.object({
  jobId: z.number().int().positive(),
  boxSerialNumber: z.string(),
  status: JobStatusSchema,
  // OPS-2892: terminal boxing station for the job. Sourced from
  // JobAudit.jobActions[].action === 'packed' (NOT jobs.boxing_station_id, which is
  // 99% null in prod). null when no `packed` event has occurred yet (in-progress or
  // audit-stale jobs) or when the station name lookup misses.
  boxingStationId: z.number().int().positive().nullable(),
  boxingStationName: z.string().nullable(),
  expected: JobReviewExpectedSchema,
  // Authoritative assigned packer; null when no worker is assigned.
  packer: JobReviewPackerSchema.nullable().default(null),
  // One entry per packing session (empty for audit-stale jobs).
  sessions: z.array(JobReviewSessionSchema).default([]),
})

export const JobReviewDTOHappySchema = JobReviewBaseSchema.extend({
  packed: JobReviewPackedSchema,
  summary: JobReviewSummaryHappySchema,
  scanTimeline: z.array(JobReviewScanTimelineEntrySchema),
})
export type JobReviewDTOHappy = z.infer<typeof JobReviewDTOHappySchema>

export const JobReviewDTOStaleSchema = JobReviewBaseSchema.extend({
  packed: z.null(),
  summary: JobReviewSummaryStaleSchema,
  scanTimeline: z.array(JobReviewScanTimelineEntrySchema).length(0),
})
export type JobReviewDTOStale = z.infer<typeof JobReviewDTOStaleSchema>

export const JobReviewDTOSchema = z.union([JobReviewDTOHappySchema, JobReviewDTOStaleSchema])
export type JobReviewDTO = z.infer<typeof JobReviewDTOSchema>
